import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { EFDatePill } from "../components/EventFormPickers";
import { ConfirmModal } from "../turnos/TurnosUi";
import { useClientesApi } from "./clientesApi";
import ClienteHistorial from "./ClienteHistorial";
import ClienteNotas from "./ClienteNotas";
import { CONSENT_LABEL, MAX_TAGS, MAX_TAG_LENGTH, detalleConsent, errorDeApi, estadoConsent, fechaAR, infoDe } from "./clientesUtils";
import useCarga from "./useCarga";
import "../reservas/reservas-organizer.css";
import "./clientes.css";

/* ─────────────────────────────────────────
   ClientePage — la ficha de un cliente: datos editables, campos propios del salón, notas e historial.
   Solo se envía lo que cambió. El permiso lo decide el servidor (403 se explica en pantalla).
───────────────────────────────────────── */

const PERFIL = ["name", "email", "phone", "dni", "birthDate"];

const valorDeForm = (def, raw) => {
  if (def.type === "checkbox") return Boolean(raw);
  const texto = raw === undefined || raw === null ? "" : String(raw).trim();
  if (!texto) return null;
  return def.type === "number" ? Number(texto) : texto;
};

const campoParaInput = (def, valor) => {
  if (def.type === "checkbox") return Boolean(valor);
  return valor === undefined || valor === null ? "" : String(valor);
};

const formDe = (c, defs) => ({
  name: c.name || "",
  email: c.email || "",
  phone: c.phone || "",
  dni: c.dni || "",
  birthDate: c.birthDate || "",
  tags: Array.isArray(c.tags) ? [...c.tags] : [],
  customFields: Object.fromEntries(defs.map((d) => [d.id, campoParaInput(d, c.customFields?.[d.id])])),
});

/** `{ error, body }`: the PATCH body with only what changed (`null` when nothing changed) and the first thing wrong, if any. */
export const parchePerfil = (form, base, defs) => {
  const body = {};
  PERFIL.forEach((k) => {
    if ((form[k] || "").trim() !== (base[k] || "")) body[k] = form[k].trim();
  });
  if (JSON.stringify(form.tags) !== JSON.stringify(base.tags || [])) body.tags = form.tags;
  const campos = {};
  defs.forEach((d) => {
    const nuevo = valorDeForm(d, form.customFields[d.id]);
    const viejo = valorDeForm(d, base.customFields?.[d.id]);
    if (JSON.stringify(nuevo) !== JSON.stringify(viejo)) campos[d.id] = nuevo;
  });
  if (Object.keys(campos).length) body.customFields = campos;

  const badNumber = defs.find((d) => d.type === "number" && form.customFields[d.id] !== "" && !Number.isFinite(Number(form.customFields[d.id])));
  let error = "";
  if (!form.name.trim()) error = "Ingresá el nombre del cliente.";
  else if (form.email.trim() && !/^\S+@\S+\.\S+$/.test(form.email.trim())) error = "El email no es válido.";
  else if (form.tags.length > MAX_TAGS) error = `Hasta ${MAX_TAGS} etiquetas.`;
  else if (badNumber) error = `"${badNumber.label}" tiene que ser un número.`;
  return { error, body: Object.keys(body).length ? body : null };
};

const mensajeDeGuardado = (e) => {
  const { message, codigo, status } = errorDeApi(e, "No pudimos guardar los cambios. Probá de nuevo.");
  if (codigo === "too_many_tags") return `Hasta ${MAX_TAGS} etiquetas de ${MAX_TAG_LENGTH} caracteres cada una.`;
  if (status === 400 && codigo === "invalid_field" && /dni/i.test(message)) return "El DNI tiene que tener entre 7 y 10 números.";
  if (status === 409 && codigo === "dni_taken") return "Ya hay otra ficha de este negocio con ese DNI.";
  if (status === 403) return "No tenés permiso para editar fichas de cliente.";
  if (status === 404) return "Esta ficha ya no existe. Volvé a la lista.";
  return message;
};

const CampoPropio = ({ def, value, onChange }) => {
  const id = `fc-cf-${def.id}`;
  if (def.type === "checkbox") {
    return (
      <label className="fc-check fc-full" htmlFor={id}>
        <input id={id} type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        <span>{def.label}</span>
      </label>
    );
  }
  let input;
  if (def.type === "longtext") {
    input = <textarea id={id} className="zr-textarea" style={{ minHeight: 80 }} maxLength={2000} value={value} onChange={(e) => onChange(e.target.value)} />;
  } else if (def.type === "select") {
    input = (
      <select id={id} className="zr-input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Sin elegir</option>
        {(def.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else {
    input = (
      <input
        id={id}
        className="zr-input"
        type={def.type === "number" ? "number" : def.type === "date" ? "date" : "text"}
        maxLength={def.type === "text" ? 200 : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <label className={"zr-field" + (def.type === "longtext" ? " fc-full" : "")} htmlFor={id}>
      <span className="zr-field__label">{def.label}</span>
      {input}
    </label>
  );
};

const ClientePage = ({ clientId, onVolver, onArchivado, volverLabel = "Volver a clientes" }) => {
  const api = useClientesApi();
  const perfil = useCarga(() => api.get(clientId), clientId, {
    valida: (info) => Boolean(info._id),
    mensaje: "No pudimos cargar la ficha. Probá de nuevo.",
  });
  const { data } = perfil;
  const defs = useMemo(() => (data?.fieldDefinitions || []).filter((d) => d && d.id && d.archived !== true), [data?.fieldDefinitions]);

  const [form, setForm] = useState(null);
  const [etiqueta, setEtiqueta] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState("");
  const [aviso, setAviso] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [errorArchivo, setErrorArchivo] = useState("");
  const [confirmandoConsent, setConfirmandoConsent] = useState(false);
  const [guardandoConsent, setGuardandoConsent] = useState(false);
  const [errorConsent, setErrorConsent] = useState("");

  useEffect(() => {
    if (data) setForm(formDe(data, defs));
    // The form restarts from the record, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const set = (patch) => {
    setAviso("");
    setForm((prev) => ({ ...prev, ...patch }));
  };

  const agregarEtiqueta = () => {
    const t = etiqueta.trim();
    if (!t) return;
    if (t.length > MAX_TAG_LENGTH) {
      setErrorForm(`Cada etiqueta puede tener hasta ${MAX_TAG_LENGTH} caracteres.`);
      return;
    }
    if (form.tags.length >= MAX_TAGS) {
      setErrorForm(`Hasta ${MAX_TAGS} etiquetas.`);
      return;
    }
    setErrorForm("");
    if (!form.tags.some((x) => x.toLowerCase() === t.toLowerCase())) set({ tags: [...form.tags, t] });
    setEtiqueta("");
  };

  const guardar = async (e) => {
    e.preventDefault();
    const { error, body } = parchePerfil(form, data, defs);
    if (error) {
      setErrorForm(error);
      return;
    }
    if (!body) return;
    setErrorForm("");
    setGuardando(true);
    try {
      const vista = infoDe(await api.update(clientId, body));
      if (!vista?._id) throw new Error("Invalid client response");
      perfil.setData((prev) => ({ ...prev, ...vista }));
      setAviso("Cambios guardados.");
    } catch (err) {
      setErrorForm(mensajeDeGuardado(err));
    } finally {
      setGuardando(false);
    }
  };

  // Consent has its own save (not part of the form): turning it on asks first, turning it off goes straight through.
  const cambiarConsent = async (acepta) => {
    setGuardandoConsent(true);
    setErrorConsent("");
    try {
      const vista = infoDe(await api.update(clientId, { marketingConsent: acepta }));
      if (!vista?._id) throw new Error("Invalid client response");
      const marketingConsent = vista.marketingConsent || { status: acepta ? "opted_in" : "opted_out", at: new Date().toISOString(), source: "organizer" };
      perfil.setData((prev) => ({ ...prev, marketingConsent }));
      setConfirmandoConsent(false);
    } catch (err) {
      setConfirmandoConsent(false);
      const { message, status } = errorDeApi(err, "No pudimos guardar el consentimiento. Probá de nuevo.");
      setErrorConsent(status === 403 ? "No tenés permiso para editar fichas de cliente." : message);
    } finally {
      setGuardandoConsent(false);
    }
  };

  const archivar = async () => {
    setGuardando(true);
    setErrorArchivo("");
    try {
      await api.archive(clientId);
      setConfirmando(false);
      onArchivado();
    } catch (err) {
      const { message, status } = errorDeApi(err, "No pudimos archivar la ficha. Probá de nuevo.");
      setErrorArchivo(status === 403 ? "No tenés permiso para archivar fichas de cliente." : message);
    } finally {
      setGuardando(false);
    }
  };

  const desarchivar = async () => {
    setGuardando(true);
    setErrorForm("");
    try {
      await api.unarchive(clientId);
      perfil.setData((prev) => ({ ...prev, status: "active" }));
      setAviso("Cliente desarchivado.");
    } catch (err) {
      const { message, status } = errorDeApi(err, "No pudimos desarchivar la ficha. Probá de nuevo.");
      setErrorForm(status === 403 ? "No tenés permiso para desarchivar fichas de cliente." : message);
    } finally {
      setGuardando(false);
    }
  };

  const archivado = data?.status === "archived";
  const cambios = Boolean(form && data && parchePerfil(form, data, defs).body);

  return (
    <div className="fc-page">
      <div className="fc-page__head">
        {volverLabel && <button type="button" className="zr-btn zr-btn--soft" onClick={onVolver}>{volverLabel}</button>}
        <h2>{data ? data.name || "Cliente sin nombre" : "Ficha del cliente"}</h2>
        {archivado && <span className="zr-badge">Archivado</span>}
      </div>

      {perfil.loading && !data && <p role="status">Cargando ficha…</p>}
      {perfil.error && !data && (
        <div className="fc-error" role="alert">
          {perfil.error.status === 404 ? "No encontramos esta ficha. Puede que se haya archivado." : perfil.error.message}
          {perfil.error.status !== 404 && <button type="button" className="zr-btn zr-btn--soft" onClick={perfil.reload}>Reintentar</button>}
        </div>
      )}

      {data && form && (
        <>
          <section className="fc-card" aria-labelledby="fc-datos-title">
            <h3 id="fc-datos-title">Datos del cliente</h3>
            <div className="fc-summary">
              <span>{Number(data.summary?.appointmentsCount) === 1 ? "1 turno" : `${Number(data.summary?.appointmentsCount) || 0} turnos`}</span>
              <span>Primer turno: {data.summary?.firstAppointmentAt ? fechaAR(data.summary.firstAppointmentAt) : "—"}</span>
              <span>Último turno: {data.summary?.lastAppointmentAt ? fechaAR(data.summary.lastAppointmentAt) : "—"}</span>
              {data.hasAccount && <span>Tiene cuenta de Zoco</span>}
            </div>

            <div className="fc-consent" style={{ marginTop: 12 }}>
              <label className="fc-toggle">
                <input
                  type="checkbox"
                  checked={estadoConsent(data.marketingConsent) === "opted_in"}
                  disabled={guardandoConsent}
                  onChange={(e) => (e.target.checked ? setConfirmandoConsent(true) : cambiarConsent(false))}
                />
                Recibe novedades y promociones
              </label>
              <p className="fc-hint" style={{ margin: "6px 0 0" }}>
                <strong>{CONSENT_LABEL[estadoConsent(data.marketingConsent)]}</strong>
                {detalleConsent(data.marketingConsent) && <span> · {detalleConsent(data.marketingConsent)}</span>}
              </p>
              {errorConsent && <div className="fc-error" role="alert">{errorConsent}</div>}
            </div>

            <form onSubmit={guardar} noValidate>
              <div className="fc-form-grid" style={{ marginTop: 16 }}>
                <label className="zr-field">
                  <span className="zr-field__label">Nombre</span>
                  <input className="zr-input" value={form.name} maxLength={120} onChange={(e) => set({ name: e.target.value })} />
                </label>
                <label className="zr-field">
                  <span className="zr-field__label">Email</span>
                  <input className="zr-input" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
                </label>
                <label className="zr-field">
                  <span className="zr-field__label">Teléfono</span>
                  <input className="zr-input" type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} />
                </label>
                <label className="zr-field">
                  <span className="zr-field__label">DNI</span>
                  <input className="zr-input" inputMode="numeric" value={form.dni} onChange={(e) => set({ dni: e.target.value })} />
                </label>
                <div className="zr-field">
                  <span className="zr-field__label">Cumpleaños</span>
                  <EFDatePill
                    value={form.birthDate}
                    onChange={(d) => set({ birthDate: d ? d.format("YYYY-MM-DD") : "" })}
                    title="Elegir cumpleaños"
                    quickPicks={false}
                    clearable
                    disabledDate={(d) => d.isAfter(dayjs(), "day")}
                    defaultPickerValue={form.birthDate ? undefined : dayjs().subtract(30, "year")}
                  />
                </div>

                <div className="zr-field fc-full">
                  <span className="zr-field__label">Etiquetas (hasta {MAX_TAGS})</span>
                  {form.tags.length > 0 && (
                    <div className="fc-tags">
                      {form.tags.map((t) => (
                        <span key={t} className="fc-tag">
                          {t}
                          <button type="button" aria-label={`Quitar etiqueta ${t}`} onClick={() => set({ tags: form.tags.filter((x) => x !== t) })}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="fc-tag-input">
                    <input
                      className="zr-input"
                      aria-label="Nueva etiqueta"
                      placeholder="Ej: VIP, alérgica al látex"
                      maxLength={MAX_TAG_LENGTH}
                      value={etiqueta}
                      onChange={(e) => setEtiqueta(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          agregarEtiqueta();
                        }
                      }}
                    />
                    <button type="button" className="zr-btn zr-btn--soft" onClick={agregarEtiqueta}>Agregar etiqueta</button>
                  </div>
                </div>

                {defs.length > 0 && <h3 className="fc-full" style={{ margin: "8px 0 0" }}>Campos del salón</h3>}
                {defs.map((d) => (
                  <CampoPropio
                    key={d.id}
                    def={d}
                    value={form.customFields[d.id]}
                    onChange={(v) => set({ customFields: { ...form.customFields, [d.id]: v } })}
                  />
                ))}
              </div>

              {errorForm && <div className="fc-error" role="alert">{errorForm}</div>}
              {aviso && <p className="fc-hint" role="status">{aviso}</p>}
              <div className="fc-actions">
                <button type="submit" className="zr-btn zr-btn--dark" disabled={guardando || !cambios}>
                  {guardando ? "Guardando…" : "Guardar cambios"}
                </button>
              </div>
            </form>
          </section>

          <ClienteNotas clientId={clientId} />
          <ClienteHistorial clientId={clientId} />

          <div className="fc-actions">
            {archivado ? (
              <button type="button" className="zr-btn zr-btn--soft" disabled={guardando} onClick={desarchivar}>Desarchivar</button>
            ) : (
              <button
                type="button"
                className="zr-btn zr-btn--ghost"
                onClick={() => {
                  setErrorArchivo("");
                  setConfirmando(true);
                }}
              >
                Archivar cliente
              </button>
            )}
          </div>
        </>
      )}

      {confirmandoConsent && (
        <ConfirmModal title="Novedades y promociones" confirmLabel="Confirmar" busy={guardandoConsent} onConfirm={() => cambiarConsent(true)} onCancel={() => setConfirmandoConsent(false)}>
          Confirmá que el cliente aceptó recibir novedades.
        </ConfirmModal>
      )}

      {confirmando && (
        <ConfirmModal title="Archivar cliente" confirmLabel="Archivar" busy={guardando} onConfirm={archivar} onCancel={() => setConfirmando(false)}>
          La ficha deja de aparecer en la lista de clientes y en los turnos. No se borra ningún dato.
          {errorArchivo && <span className="fc-field-error" role="alert"> {errorArchivo}</span>}
        </ConfirmModal>
      )}
    </div>
  );
};

export default ClientePage;
